/**
 * WorkstationAdPanel – la pestaña «Active Directory» de la ficha del equipo.
 *
 * Enseña el equipo **desde su dominio**, no el dominio en sí:
 *
 *   · si está unido y con qué DC habla (canal seguro, sitio, autenticación),
 *   · dónde está su objeto en el árbol (DN y OU),
 *   · a qué grupos pertenece,
 *   · qué GPO le están aplicando,
 *   · y cuántas claves de recuperación de BitLocker hay publicadas en AD.
 *
 * Los datos los manda el agente (`ad_client`), no los inventa la web. Si el
 * agente aún no los ha recogido, la pestaña lo dice y ofrece pedirlos.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  FolderOpenIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import HelpDot from '../academy/HelpDot';
import { versionAtLeast } from '../utils/version';

const GPO_RESULT = {
  ok: { label: 'Correcta', pill: 'sf2-pill--ok' },
  failed: { label: 'Advertencia', pill: 'sf2-pill--wa' },
  unknown: { label: 'Sin dato', pill: '' },
};

function formatMoment(value) {
  if (!value) return '—';
  const raw = String(value);
  const parsed = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('es-ES');
}

function Field({ label, value, mono = false }) {
  return (
    <div className="sf2-field">
      <label>{label}</label>
      <div className="v" style={mono ? { fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' } : undefined}>
        {value || '—'}
      </div>
    </div>
  );
}

function Pill({ tone = '', children }) {
  return <span className={`sf2-pill ${tone}`}>{children}</span>;
}

export default function WorkstationAdPanel({ companyId, workstation, ciType = 'workstation' }) {
  const ad = workstation?.ad_client || null;
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [commandId, setCommandId] = useState(null);
  const mountedRef = useRef(true);
  const pollRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  const enqueue = useCallback(
    async (kind) => {
      if (!companyId || !workstation?.agent_id) return null;
      const payload =
        kind === 'gpupdate'
          ? { cmd: 'gpupdate /force' }
          : kind === 'rsop'
            ? {
                cmd:
                  'powershell -NoProfile -Command "'
                  + "$p = Join-Path $env:ProgramData \\'cmdb-rsop.html\\'; "
                  + 'gpresult /h $p /f | Out-Null; '
                  + "if (Test-Path -LiteralPath $p) { Write-Output ('Informe generado: ' + $p) } "
                  + "else { Write-Output 'No se pudo generar el informe' }"
                  + '"',
              }
            : null;
      const res = await api.enqueueAgentCommand(companyId, {
        agent_id: workstation.agent_id,
        command: kind === 'ad' ? 'ad_refresh' : 'exec',
        ci_type: ciType,
        ci_id: workstation.id,
        payload: payload || {},
      });
      return res?.command?.id || null;
    },
    [companyId, workstation?.agent_id, workstation?.id, ciType],
  );

  const run = async (kind, message) => {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      const id = await enqueue(kind);
      setNotice(message);
      if (kind === 'ad' && id) setCommandId(id);
    } catch (e) {
      setError(e?.message || 'No se pudo encolar la acción');
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  // «Consultar AD»: cuando el agente termina, se avisa para recargar la ficha.
  useEffect(() => {
    if (!commandId || !companyId) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const payload = await api.getAgentCommandStatus(companyId, commandId);
        const status = payload?.command?.status;
        if (status === 'done' || status === 'error') {
          window.clearInterval(timer);
          if (!mountedRef.current) return;
          setCommandId(null);
          setNotice(
            status === 'done'
              ? 'AD consultado. Recarga la ficha para ver los datos nuevos.'
              : 'El agente no pudo consultar AD. Revisa la pestaña Agente para ver el motivo.',
          );
        }
      } catch {
        // Silencio: el aviso ya está en la lista de ejecuciones.
      }
    }, 10000);
    pollRef.current = timer;
    return () => window.clearInterval(timer);
  }, [commandId, companyId]);

  const online = workstation?.agent_status === 'online'
    || Boolean(workstation?.agent_id);

  // `ad_refresh` no existe en el agente 2.6.3: en vez de dejar que falle con
  // «Unknown command», se avisa antes.
  const soportaAdRefresh = versionAtLeast(workstation?.agent_version, '2.6.4');
  const avisoVersion = soportaAdRefresh
    ? null
    : `El agente instalado es ${workstation?.agent_version || 'desconocido'}: consultar Active Directory necesita 2.6.4 o superior. Actualiza el agente desde esta ficha.`;

  if (!ad) {
    return (
      <div className="dv-tab-pane">
        <div className="sf2-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Active Directory</h3>
            <HelpDot topic="listados.agent_ad" label="Ayuda: Active Directory del equipo" />
          </div>
          <p className="sf2-hint" style={{ marginTop: 0 }}>
            El agente todavía no ha enviado los datos de Active Directory de este equipo.
            Pídelos y aparecerán aquí en cuanto responda.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="dv-btn dv-btn-secondary"
              onClick={() => run('ad', 'Consulta de AD encolada. El agente la ejecutará en su próximo sondeo.')}
              disabled={busy !== null || !online || !soportaAdRefresh}
              title={avisoVersion || 'Vuelve a leer el dominio, la OU, los grupos y las GPO'}
            >
              <ArrowPathIcon /> {busy === 'ad' ? 'Encolando…' : 'Consultar AD'}
            </button>
          </div>
          {avisoVersion && (
            <p className="sf2-hint" style={{ marginBottom: 0, color: 'var(--amber-500, #f59e0b)' }}>
              {avisoVersion}
            </p>
          )}
          {notice && <p className="sf2-hint" style={{ marginBottom: 0 }}>{notice}</p>}
          {error && <Pill tone="sf2-pill--bad">{error}</Pill>}
        </div>
      </div>
    );
  }

  const unido = Boolean(ad.part_of_domain);
  const trustOk = ad.secure_channel === true;
  const gpos = Array.isArray(ad.gpos) ? ad.gpos : [];
  const groups = Array.isArray(ad.groups) ? ad.groups : [];
  const dnsServers = Array.isArray(ad.dns_servers) ? ad.dns_servers : [];
  const bitlocker = ad.bitlocker || { keys: [] };
  const ouPath = Array.isArray(ad.ou_path) ? ad.ou_path : [];

  return (
    <div className="dv-tab-pane">
      {/* KPIs */}
      <div className="sf2-hl" style={{ marginBottom: 12 }}>
        <div className="sf2-kpi">
          <span>Unido a dominio</span>
          <b style={{ fontSize: '0.95rem', color: unido ? 'var(--green-600)' : 'var(--red-600)' }}>
            {unido ? 'Sí' : 'No'}
          </b>
        </div>
        <div className="sf2-kpi"><span>Dominio</span><b style={{ fontSize: '0.95rem' }}>{ad.domain_dns || ad.domain_netbios || '—'}</b></div>
        <div className="sf2-kpi"><span>DC autenticador</span><b style={{ fontSize: '0.95rem' }}>{ad.dc_name || '—'}</b></div>
        <div className="sf2-kpi"><span>Último contacto</span><b style={{ fontSize: '0.95rem' }}>{formatMoment(ad.collected_at)}</b></div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="sf2-card" style={{ flex: '1 1 420px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Información de dominio</h3>
            <HelpDot topic="listados.agent_ad" label="Ayuda: Active Directory del equipo" />
          </div>
          <div className="sf2-fields" style={{ '--n': 2 }}>
            <Field label="Nombre DNS" value={ad.domain_dns} />
            <Field label="Nombre NetBIOS" value={ad.domain_netbios} />
            <div className="sf2-field">
              <label>Estado de confianza</label>
              <div className="v">
                <Pill tone={trustOk ? 'sf2-pill--ok' : 'sf2-pill--wa'}>
                  {trustOk ? 'Correcta' : ad.secure_channel === false ? 'Rota' : 'Sin comprobar'}
                </Pill>
              </div>
            </div>
            <div className="sf2-field">
              <label>Canal seguro</label>
              <div className="v">
                <Pill tone={trustOk ? 'sf2-pill--ok' : 'sf2-pill--wa'}>
                  {trustOk ? 'Establecido' : ad.secure_channel === false ? 'No establecido' : 'Sin comprobar'}
                </Pill>
              </div>
            </div>
            <Field
              label="Usuario actual"
              value={ad.current_user || workstation?.assigned_to}
            />
            <Field label="SID equipo" value={ad.computer_sid} mono />
          </div>
        </div>

        <div className="sf2-card" style={{ flex: '1 1 420px' }}>
          <h3>Controlador de dominio</h3>
          <div className="sf2-fields" style={{ '--n': 2 }}>
            <Field label="DC que valida" value={ad.dc_fqdn || ad.dc_name} />
            <Field label="Dirección IP" value={ad.dc_ip} mono />
            <Field label="Sitio AD" value={ad.dc_site} />
            <Field label="Autenticación" value={ad.auth} />
            <Field label="Última validación" value={formatMoment(ad.collected_at)} />
            <Field label="DNS configurado" value={dnsServers.join(', ')} mono />
          </div>
        </div>
      </div>

      {/* Ubicación en el árbol */}
      <div className="sf2-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <FolderOpenIcon style={{ width: 18, height: 18 }} />
          <h3 style={{ margin: 0 }}>Ubicación en Active Directory</h3>
          <button
            type="button"
            className="dv-btn dv-btn-secondary"
            style={{ marginLeft: 'auto' }}
            onClick={() => run('ad', 'Consulta de AD encolada. El agente la ejecutará en su próximo sondeo.')}
            disabled={busy !== null || !online || !soportaAdRefresh}
            title={avisoVersion || 'Vuelve a leer el objeto del equipo en AD'}
          >
            <ArrowPathIcon /> {busy === 'ad' ? 'Consultando…' : 'Consultar AD'}
          </button>
        </div>
        <div className="sf2-field">
          <label>Distinguished Name (OU)</label>
          <div className="v" style={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>
            {ad.dn || '—'}
          </div>
        </div>
        {ouPath.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8, fontSize: '0.82rem' }}>
            <span className="sf2-hint" style={{ margin: 0 }}>Árbol organizativo:</span>
            {ouPath.map((node, index) => (
              <React.Fragment key={`${node}-${index}`}>
                {index > 0 && <span style={{ color: 'var(--text-muted)' }}>›</span>}
                {index === ouPath.length - 1 ? (
                  <Pill tone="sf2-pill--ac">{node}</Pill>
                ) : (
                  <span>{node}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* GPO */}
      <div className="sf2-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Directivas de grupo (GPO)</h3>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="dv-btn dv-btn-secondary"
              onClick={() => run('gpupdate', '«gpupdate /force» encolado. Puede tardar un par de minutos; el resultado sale en la pestaña Agente.')}
              disabled={busy !== null || !online}
              title="Aplica las directivas ahora en el equipo"
            >
              <ShieldCheckIcon /> {busy === 'gpupdate' ? 'Encolando…' : 'Forzar actualización GPO'}
            </button>
            <button
              type="button"
              className="dv-btn dv-btn-secondary"
              onClick={() => run('rsop', 'Informe RSoP encolado. La ruta del HTML aparecerá en la pestaña Agente.')}
              disabled={busy !== null || !online}
              title="Genera el informe HTML de directivas en el equipo"
            >
              <ArrowTopRightOnSquareIcon /> {busy === 'rsop' ? 'Encolando…' : 'Generar informe RSoP'}
            </button>
          </div>
        </div>

        {gpos.length === 0 ? (
          <p className="sf2-hint" style={{ margin: 0 }}>
            El agente no ha devuelto directivas. Puede que el equipo no tenga GPO de equipo o
            que `gpresult` no haya podido generar el informe.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Directiva aplicada</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Vinculada a</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Informe</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {gpos.map((gpo, index) => {
                  const result = GPO_RESULT[gpo.result] || GPO_RESULT.unknown;
                  return (
                    <tr key={`${gpo.name}-${index}`} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px' }}>{gpo.name}</td>
                      <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{gpo.linked_to || '—'}</td>
                      <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{formatMoment(ad.gpo_report_at)}</td>
                      <td style={{ padding: '6px 8px' }}><Pill tone={result.pill}>{result.label}</Pill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div className="sf2-card" style={{ flex: '1 1 420px' }}>
          <h3>Grupos del equipo en AD</h3>
          {groups.length === 0 ? (
            <p className="sf2-hint" style={{ margin: 0 }}>Sin grupos reportados.</p>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {groups.map((group) => <Pill key={group}>{group}</Pill>)}
            </div>
          )}
        </div>

        <div className="sf2-card" style={{ flex: '1 1 320px' }}>
          <h3>BitLocker en AD</h3>
          {bitlocker.error ? (
            <p className="sf2-hint" style={{ margin: 0 }}>
              No se pudieron leer las claves: {bitlocker.error}. Hace falta permiso de lectura
              sobre `msFVE-RecoveryInformation`.
            </p>
          ) : (bitlocker.keys || []).length === 0 ? (
            <p className="sf2-hint" style={{ margin: 0 }}>
              No hay claves de recuperación publicadas para este equipo.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              <Pill tone="sf2-pill--ok">
                {bitlocker.keys.length} clave{bitlocker.keys.length === 1 ? '' : 's'} publicada{bitlocker.keys.length === 1 ? '' : 's'}
              </Pill>
              {bitlocker.keys.map((key, index) => (
                <div key={key.recovery_guid || index} className="sf2-hint" style={{ margin: 0 }}>
                  {key.created ? `Creada ${formatMoment(key.created)}` : 'Sin fecha'}
                  {key.recovery_guid ? ` · ${key.recovery_guid}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {notice && <p className="sf2-hint" style={{ marginTop: 10 }}>{notice}</p>}
      {error && <Pill tone="sf2-pill--bad">{error}</Pill>}
    </div>
  );
}
